import { supabase } from '@/lib/supabase';
import type {
  ActiveBanner,
  AppRole,
  Banner,
  BannerLayout,
  BannerSeverity,
  BannerTargetKind,
} from '@/lib/types';

/**
 * Banner helpers (Day 9 / FE-01).
 */

// ----- end-user reads --------------------------------------------------------

export async function listActiveBanners(): Promise<ActiveBanner[]> {
  const { data, error } = await supabase.rpc('banners_active_for_me');
  if (error) throw error;
  return (data ?? []) as ActiveBanner[];
}

export async function dismissBanner(id: string, wasAcknowledgement = false): Promise<void> {
  const { error } = await supabase.rpc('banner_dismiss', {
    p_id: id,
    p_was_acknowledgement: wasAcknowledgement,
  });
  if (error) throw error;
}

/** Fire-and-forget impression log. Server dedupes per (user, banner, UTC day). */
export async function logBannerView(id: string): Promise<void> {
  await supabase.rpc('banner_log_view', { p_id: id });
}

/** Fire-and-forget CTA click log. Every click is counted. */
export async function logBannerClick(id: string): Promise<void> {
  await supabase.rpc('banner_log_click', { p_id: id });
}

export interface BannerStats {
  banner_id: string;
  impressions: number;
  unique_viewers: number;
  clicks: number;
  ctr_pct: number;
  dismissals: number;
  acknowledgements: number;
  ack_rate_pct: number;
}

export async function getBannerStats(id: string): Promise<BannerStats | null> {
  const { data, error } = await supabase.rpc('admin_banner_stats', { p_id: id });
  if (error) throw error;
  const rows = (data ?? []) as BannerStats[];
  return rows[0] ?? null;
}

export async function setPromoOptOut(enabled: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc('promo_banners_set_opt_out', { p_enabled: enabled });
  if (error) throw error;
  return data as boolean;
}

// ----- admin reads / writes --------------------------------------------------

export async function listAllBanners(): Promise<Banner[]> {
  const { data, error } = await supabase.rpc('admin_banners_list');
  if (error) throw error;
  return (data ?? []) as Banner[];
}

export interface BannerCreatePayload {
  title: string;
  body_md: string;
  severity: BannerSeverity;
  starts_at: string;
  ends_at: string | null;
  cta_text?: string | null;
  cta_url?: string | null;
  target_kind: BannerTargetKind;
  target_org_ids?: string[];
  target_roles?: AppRole[];
  target_tiers?: string[];
  acknowledgement_required?: boolean;
  image_url?: string | null;
  image_alt?: string | null;
  layout?: BannerLayout;
  custom_bg_hex?: string | null;
  custom_text_hex?: string | null;
}

export async function createBanner(payload: BannerCreatePayload): Promise<Banner> {
  const { data, error } = await supabase.rpc('banner_create', {
    p_title: payload.title,
    p_body_md: payload.body_md,
    p_severity: payload.severity,
    p_starts_at: payload.starts_at,
    p_ends_at: payload.ends_at,
    p_cta_text: payload.cta_text ?? null,
    p_cta_url: payload.cta_url ?? null,
    p_target_kind: payload.target_kind,
    p_target_org_ids: payload.target_org_ids ?? [],
    p_target_roles: payload.target_roles ?? [],
    p_acknowledgement_required: payload.acknowledgement_required ?? false,
    p_image_url: payload.image_url ?? null,
    p_image_alt: payload.image_alt ?? null,
    p_layout: payload.layout ?? 'compact',
    p_target_tiers: payload.target_tiers ?? [],
    p_custom_bg_hex: payload.custom_bg_hex ?? null,
    p_custom_text_hex: payload.custom_text_hex ?? null,
    // null = inferred server-side (owner -> their org, admin -> platform banner)
    p_author_organization_id: null,
  });
  if (error) throw error;
  return data as Banner;
}

export async function updateBanner(id: string, payload: BannerCreatePayload): Promise<Banner> {
  const { data, error } = await supabase.rpc('banner_update', {
    p_id: id,
    p_title: payload.title,
    p_body_md: payload.body_md,
    p_severity: payload.severity,
    p_starts_at: payload.starts_at,
    p_ends_at: payload.ends_at,
    p_cta_text: payload.cta_text ?? null,
    p_cta_url: payload.cta_url ?? null,
    p_target_kind: payload.target_kind,
    p_target_org_ids: payload.target_org_ids ?? [],
    p_target_roles: payload.target_roles ?? [],
    p_acknowledgement_required: payload.acknowledgement_required ?? false,
    p_image_url: payload.image_url ?? null,
    p_image_alt: payload.image_alt ?? null,
    p_layout: payload.layout ?? 'compact',
    p_target_tiers: payload.target_tiers ?? [],
    p_custom_bg_hex: payload.custom_bg_hex ?? null,
    p_custom_text_hex: payload.custom_text_hex ?? null,
  });
  if (error) throw error;
  return data as Banner;
}

export async function publishBanner(id: string): Promise<Banner> {
  const { data, error } = await supabase.rpc('banner_publish', { p_id: id });
  if (error) throw error;
  return data as Banner;
}

export async function archiveBanner(id: string): Promise<Banner> {
  const { data, error } = await supabase.rpc('banner_archive', { p_id: id });
  if (error) throw error;
  return data as Banner;
}

// ----- storage upload --------------------------------------------------------

const BANNER_BUCKET = 'banner-images';

export async function uploadBannerImage(file: File): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('auth_required');

  const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
  const path = `${user.id}/${Date.now()}-${safe}`;

  const { error: upErr } = await supabase.storage
    .from(BANNER_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type || 'image/jpeg' });
  if (upErr) throw upErr;

  const { data } = supabase.storage.from(BANNER_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

// ----- presentation helpers --------------------------------------------------

/**
 * Curated palette for the admin color picker. Hex codes are colorblind-safe
 * and pair well with auto-derived white or near-black text.
 */
export const BANNER_COLOR_PALETTE: { name: string; hex: string }[] = [
  { name: 'Brand navy',    hex: '#1e3a8a' },
  { name: 'Royal blue',    hex: '#2563eb' },
  { name: 'Teal',          hex: '#0d9488' },
  { name: 'Forest',        hex: '#15803d' },
  { name: 'Mint',          hex: '#10b981' },
  { name: 'Amber',         hex: '#d97706' },
  { name: 'Orange',        hex: '#ea580c' },
  { name: 'Crimson',       hex: '#dc2626' },
  { name: 'Pink',          hex: '#db2777' },
  { name: 'Purple',        hex: '#7c3aed' },
  { name: 'Slate',         hex: '#475569' },
  { name: 'Charcoal',      hex: '#1f2937' },
];

/** WCAG-style luminance pick: returns near-black if bg is light, else white. */
export function autoTextColorForBg(hex: string): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.6 ? '#0f172a' : '#ffffff';
}

// ----- owner (org-internal) helpers ----------------------------------------

export async function listOwnerBanners(): Promise<Banner[]> {
  const { data, error } = await supabase.rpc('owner_banners_list');
  if (error) throw error;
  return (data ?? []) as Banner[];
}

export interface BannerRecipient {
  user_id: string;
  display_name: string;
  email: string | null;
  role: AppRole;
  acknowledged_at: string | null;
}

export async function getOwnerBannerRecipients(bannerId: string): Promise<BannerRecipient[]> {
  const { data, error } = await supabase.rpc('owner_banner_recipients', { p_banner_id: bannerId });
  if (error) throw error;
  return (data ?? []) as BannerRecipient[];
}

export interface CommsLogRow {
  banner_id: string;
  title: string;
  severity: BannerSeverity;
  status: 'draft' | 'published' | 'archived';
  acknowledgement_required: boolean;
  starts_at: string;
  ends_at: string | null;
  target_kind: 'my_org_all' | 'my_org_roles';
  target_roles: AppRole[] | null;
  active_recipients: number;
  acknowledged_count: number;
  dismissed_count: number;
  ack_rate_pct: number;
  published_at: string | null;
  created_at: string;
}

export async function getOwnerCommsLog(): Promise<CommsLogRow[]> {
  const { data, error } = await supabase.rpc('owner_comms_log');
  if (error) throw error;
  return (data ?? []) as CommsLogRow[];
}

export interface CommsLogDetailedRow {
  banner_id: string;
  banner_title: string;
  banner_severity: BannerSeverity;
  banner_status: 'draft' | 'published' | 'archived';
  acknowledgement_required: boolean;
  target_kind: 'my_org_all' | 'my_org_roles';
  published_at: string | null;
  user_id: string;
  recipient_name: string;
  recipient_email: string | null;
  recipient_role: AppRole;
  acknowledged_at: string | null;
  recipient_status: 'acknowledged' | 'dismissed' | 'pending';
  acknowledged_from_ip: string | null;
  acknowledged_user_agent: string | null;
}

export async function getOwnerCommsLogDetailed(): Promise<CommsLogDetailedRow[]> {
  const { data, error } = await supabase.rpc('owner_comms_log_detailed');
  if (error) throw error;
  return (data ?? []) as CommsLogDetailedRow[];
}

export const BANNER_SEVERITY_THEMES: Record<
  BannerSeverity,
  { bg: string; gradient: string; text: string; cta: string; label: string; ring: string }
> = {
  info: {
    bg: 'bg-blue-600',
    gradient: 'bg-gradient-to-r from-blue-600 via-blue-600 to-blue-700',
    text: 'text-white',
    cta: 'bg-white text-blue-700 hover:bg-blue-50',
    label: 'Info',
    ring: 'ring-blue-300/40',
  },
  warning: {
    bg: 'bg-amber-500',
    gradient: 'bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500',
    text: 'text-white',
    cta: 'bg-white text-amber-700 hover:bg-amber-50',
    label: 'Warning',
    ring: 'ring-amber-300/40',
  },
  success: {
    bg: 'bg-green-600',
    gradient: 'bg-gradient-to-r from-green-600 via-green-600 to-emerald-600',
    text: 'text-white',
    cta: 'bg-white text-green-700 hover:bg-green-50',
    label: 'Success',
    ring: 'ring-green-300/40',
  },
  promo: {
    bg: 'bg-purple-600',
    gradient: 'bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600',
    text: 'text-white',
    cta: 'bg-white text-purple-700 hover:bg-purple-50',
    label: 'Promo',
    ring: 'ring-purple-300/40',
  },
};
